import { apiService } from './apiService';
import { authEndpoints, userEndpoints } from '../api/apiEndpoint';
import { useAuthStore } from '../store/useAuthStore';
import { LoginUserInfo } from '@/common/types/login';
import { DefaultResponse } from '@/common/types/common';
import { useCompanyStore } from '@/store/useCompanyStore';

interface LoginRequest {
  loginId: string;
  password: string;
  autoSignIn?: boolean;
}

interface McpCallbackPayload {
  redirectUrl: string;
  companyCode: string;
}

export interface McpCallbackResponse {
  success: boolean;
  insertedId?: number;
  redirectUrl: string;
}

export interface RegisterUserRequest {
  notificationId?: string;
  loginId: string;
  name: string;
  phone: string;
  email: string;
  password: string;
  emailVerified?: boolean;
  userDi: string;
  userCi: string;
  token: string;
  residentNumber: string;
  company?: string;
}

export const authService = {

  async checkLogin(): Promise<DefaultResponse> {
    try {
      const response = await apiService.get<DefaultResponse>(authEndpoints.checkLogin());
      return response;
    } catch (error) {
      return {success: false, message: '로그인 상태를 확인할 수 없습니다.'};
    }
  },

  async login(data: LoginRequest): Promise<DefaultResponse | null> {
    try{      
      const response = await apiService.post<DefaultResponse>(
        authEndpoints.login(),
        { id: data.loginId, password: data.password }
      );
      if(response.success){
        await this.setUserProfile();
      }else {
        useAuthStore().logout();
      }
      return response;
    } catch (err: any) {
      console.error(err);
      return null;
    }
  },

  async mcpCallback(payload: McpCallbackPayload): Promise<McpCallbackResponse | null> {
    try {
      return await apiService.post<McpCallbackResponse>(authEndpoints.mcpCallback(), payload);
    } catch (err) {
      console.error(err);
      return null;
    }
  },

  async loginWithUserDi(userDi: string, userCi: string, token: string): Promise<DefaultResponse | null> {
    try {
      const response = await apiService.post<DefaultResponse>(authEndpoints.loginWithUserDi(), {di: userDi, ci: userCi, token: token});
      console.log('loginWithUserDi', response);
      return response;
    } catch (error) {
      return null;
    }
  },

  async setUserProfile(): Promise<{success: boolean, data?: LoginUserInfo}> {
    try {
      const userProfileResult = await apiService.get<LoginUserInfo>(userEndpoints.getUserProfile());
      if(userProfileResult){
        useAuthStore.getState().setUser(userProfileResult);    
        useCompanyStore.getState().setCompanies(userProfileResult.companyUsers);          
        return {success: true, data: userProfileResult};
      }
      return {success: false};
    } catch (error) {
      return {success: false};
    }
  },

  async getRepresentativeCompany(type: 'portfolioInfo' | 'coldcall' | 'innovate'): Promise<LoginUserInfo | null> {
    try {
      const response = await apiService.get<LoginUserInfo>(userEndpoints.getRepresentativeCompany(type));
      return response;
    } catch (error) {
      return null;
    }
  },

  async logout(): Promise<DefaultResponse | null> {
    const response = await apiService.post<DefaultResponse>(authEndpoints.logout());
    useAuthStore.getState().logout();
    // useCompanyStore.getState().clearCompanyState();
    return response;
  },
  
  async registerUser(data: RegisterUserRequest): Promise<DefaultResponse | null> {
    const response = await apiService.post<DefaultResponse>(authEndpoints.authRegister(), data);
    return response;
  },

  async checkDuplicate(phone: string): Promise<{isDuplicate: boolean, email: string} | null> {
    const response = await apiService.post<{isDuplicate: boolean, email: string}>(authEndpoints.checkDuplicate(), {phone});
    return response;
  },

  async checkEmailDuplicate(email: string): Promise<{isDuplicate: boolean, email: string} | null> {
    try {
      const response = await apiService.post<{isDuplicate: boolean, email: string}>(authEndpoints.checkEmailDuplicate(), {email});
      return response;
    } catch (error) {
      return null;
    }

  },

  async inisisRequest(): Promise<DefaultResponse | null> {
    const response = await apiService.get<DefaultResponse>(authEndpoints.inisisRequest());
    return response;
  },

  async checkDuplicateByDi(di: string, ci: string, token: string): Promise<{isDuplicate: boolean, name: string, phone: string} | null> {
    const response = await apiService.post<{isDuplicate: boolean, name: string, phone: string}>(authEndpoints.checkDuplicateByDi(), {di, ci, token});
    return response;
  },

  async verifyEmail(token: string): Promise<DefaultResponse> {
    const response = await apiService.post<DefaultResponse>(authEndpoints.verifyEmail(token));
    return response;
  },

  async resendVerifyEmail(email: string, companyUserId?: number): Promise<DefaultResponse> {
    const body = {email, companyUserId};
    const response = await apiService.post<DefaultResponse>(authEndpoints.resendVerifyEmail(), body);
    return response;
  },
};
